import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { EventPricingMatrix, NonRepeatingEvent } from '../../../../../../../models/event-type';
import { PrivateEventBooking } from '../../../../../../../models/scheduled-event';
import { ScheduledEventService } from '../../../../../../../services/scheduled-event/scheduled-event.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../utils';
import { PaymentService } from '../../../../../../../services/payment/payment.service';
import { PrivateEventSelectPaymentAmountComponent } from './modals/private-event-select-payment-amount/private-event-select-payment-amount.component';
import { PrivateEventRefundExcessModalComponent } from './modals/private-event-refund-excess-modal/private-event-refund-excess-modal.component';
import { PrivateEventRequestPaymentModalComponent } from './modals/private-event-request-payment-modal/private-event-request-payment-modal.component';
import { PrivateEventResendRequestedPaymentEmailModalComponent } from './modals/private-event-resend-requested-payment-email-modal/private-event-resend-requested-payment-email-modal.component';
import { displayMXMerchantCancelError, initPayment } from '../../../../modals/utils';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { PaymentProcessorType } from '../../../../../../../models/organization-config-enums';
import * as liveReaderRefusal from '../../../../modals/live-reader-refusal';

declare const emergepay: any;


@Component({
  selector: 'app-private-event-edit-payment',
  templateUrl: './private-event-edit-payment.component.html',
  styleUrls: ['./private-event-edit-payment.component.css']
})
export class PrivateEventEditPaymentComponent implements OnInit, OnDestroy {

  // How long the "Payment link copied." confirmation stays up.
  private static readonly COPIED_CONFIRMATION_MS = 1500;

  public privateEvent: NonRepeatingEvent;
  public pricingMatrix: EventPricingMatrix;
  public booking: PrivateEventBooking;

  public errorMessage = '';
  public errors: any = {};
  public successMessage = '';

  public loadingPrivateEvent = true;
  public loadingPricingMatrix = false;
  public loadingBooking = false;
  public loadingPayment = false;

  public privateEventNotFound = false;
  public missingPermission = false;
  public serverError: number = null;

  public requestedPaymentLinkCopied = false;
  private copiedResetTimer: any = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;
  public parseFloat = parseFloat;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private paymentService: PaymentService,
    private modalService: NgbModal,
    private clipboard: Clipboard
  ) {}

  ngOnDestroy() {
    clearTimeout(this.copiedResetTimer);
  }

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_events) {
      this.missingPermission = true;
      return;
    }

    displayMXMerchantCancelError(this.modalService, this.route, this.router);

    const routeParams = this.route.snapshot.params;

    this.loadPrivateEvent(routeParams.id);
  }

  public setEmptyBooking() {
    this.booking = {
      booked_by_id: null,
      booked_by_first_name: null,
      booked_by_last_name: null,
      booked_by_birthday: null,
      booked_by_email: null,
      payment_fee_name: null,
      add_on_names: [],
      per_booking_base_fee: '0.00',
      per_booking_add_on_fee: '0.00',
      total_fee_pre_tax: '0.00',
      total_fee: '0.00',
      discount_amount: '0.00',
      total_fee_without_discount: '0.00',
      amount_due: '0.00',
      amount_paid: '0.00',
      amount_refunded: '0.00',
      invoice_id: null,
      payment_processor_type: null,
      payments: [],
      pending_requested_payment: null,
      reservations: []
    };
  }

  public loadPrivateEvent(eventTypeId: string) {
    this.loadingPrivateEvent = true;

    const eventIdInt = parseInt(eventTypeId, 10);
    if (isNaN(eventIdInt)) {
      this.privateEventNotFound = true;
      this.loadingPrivateEvent = false;
      return;
    }

    this.eventTypeService
      .getNonRepeatingEvent(eventIdInt)
      .then(eventType => {
        this.privateEvent = eventType;

        if (!this.privateEvent.is_private) {
          this.privateEventNotFound = true;
          return;
        }

        this.loadPricingMatrix();
        this.loadBooking();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPrivateEvent = false; });
  }

  public loadPricingMatrix() {
    this.loadingPricingMatrix = true;

    if (!this.privateEvent.pricing_matrix_id) {
      this.pricingMatrix = null;
      this.loadingPricingMatrix = false;
      return;
    }

    this.eventTypeService
      .getEventPricingMatrix(
        this.privateEvent.event_type_id,
        this.privateEvent.pricing_matrix_id
      )
      .then(matrix => {
        this.pricingMatrix = matrix;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPricingMatrix = false; });
  }

  public loadBooking() {
    this.loadingBooking = true;

    this.scheduledEventService
      .getPrivateEventBooking(this.privateEvent.id)
      .then(booking => {
        this.booking = booking;

        if (this.booking.payment_processor_type === PaymentProcessorType.gravity) {
          emergepay.init();
        }
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.setEmptyBooking();
        } else if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingBooking = false; });
  }

  /**
   * Get the link back to the private event's details view.
   */
  public getBackLink(): string {
    if (this.privateEvent && (this.privateEvent.event_parts.length > 0)) {
      return '/events/scheduled/now/event/' + this.privateEvent.event_parts[0].id.toString();
    } else {
      return '/events/scheduled/now';
    }
  }

  public isLoading(): boolean {
    return (
      this.loadingPrivateEvent ||
      this.loadingPricingMatrix ||
      this.loadingBooking
    );
  }

  public handlePayNow() {
    const modalRef = this.modalService.open(PrivateEventSelectPaymentAmountComponent);
    modalRef.componentInstance.booking = this.booking;
    modalRef.componentInstance.submitCallback = (amount?: number) => {
      initPayment(
        this.modalService,
        this.booking.invoice_id,
        this.booking.payment_processor_type,
        this.booking.booked_by_id,
        'Capitan private event reservation #' + this.booking.invoice_id.toString() + ': ' + this.privateEvent.event_type_name,
        location.href,
        location.href,
        () => { this.loadPrivateEvent(this.privateEvent.id.toString()); },
        amount
      );
    };
  }

  public handleRefundExcess() {
    const modalRef = this.modalService.open(PrivateEventRefundExcessModalComponent);
    modalRef.componentInstance.booking = this.booking;
    modalRef.componentInstance.submitCallback = (amount: number) => {
      this.loadingPayment = true;

      this.paymentService
        .refundInvoice(
          this.booking.invoice_id,
          this.booking.booked_by_id,
          amount,
          true
        )
        .then(() => {
          this.loadPrivateEvent(this.privateEvent.id.toString());
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingPayment = false; });
    };
  }

  public handleRequestPayment() {
    const amountModalRef = this.modalService.open(PrivateEventSelectPaymentAmountComponent);
    amountModalRef.componentInstance.booking = this.booking;
    amountModalRef.componentInstance.submitCallback = (amount?: number) => {
      const requestModalRef = this.modalService.open(PrivateEventRequestPaymentModalComponent, { size: 'lg' });
      requestModalRef.componentInstance.booking = this.booking;
      requestModalRef.componentInstance.amount = (amount === undefined) ? null : amount;
      requestModalRef.componentInstance.submitCallback = () => {
        this.loadBooking();
      };
    };
  }

  /**
   * Copy the pending requested payment's login-free payment link to the
   * clipboard, so staff can text it, paste it into their own email thread, or
   * open it on a front-desk device (PRO-1387). The URL is built server-side.
   */
  public handleCopyRequestedPaymentLink() {
    if (!this.booking.pending_requested_payment) {
      return;
    }
    this.clipboard.copy(this.booking.pending_requested_payment.payment_url);
    this.requestedPaymentLinkCopied = true;
    clearTimeout(this.copiedResetTimer);
    this.copiedResetTimer = setTimeout(
      () => { this.requestedPaymentLinkCopied = false; },
      PrivateEventEditPaymentComponent.COPIED_CONFIRMATION_MS
    );
  }

  public handleResendRequestedPaymentEmail() {
    if (!this.booking.pending_requested_payment) {
      return;
    }
    const modalRef = this.modalService.open(PrivateEventResendRequestedPaymentEmailModalComponent);
    modalRef.componentInstance.requestedPaymentId = this.booking.pending_requested_payment.id;
    modalRef.componentInstance.bookingCustomerEmail = this.booking.booked_by_email;
    modalRef.componentInstance.hasBeenEmailed =
      this.booking.pending_requested_payment.has_been_emailed;
    modalRef.componentInstance.submitCallback = () => {
      this.loadBooking();
    };
  }

  public handleCancelRequestedPayment() {
    if (!this.booking.pending_requested_payment) {
      return;
    }
    const requestedPaymentId = this.booking.pending_requested_payment.id;
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Cancel Requested Payment';
    modalRef.componentInstance.messageText = 'Are you sure you want to cancel this requested payment? The customer will no longer be able to pay using the link they were sent.';
    modalRef.componentInstance.messageAlertClass = 'alert-info';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingPayment = true;

      this.paymentService
        .cancelRequestedPayment(requestedPaymentId)
        .then(() => {
          this.loadBooking();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else if (reason.status === 400 && reason.error && reason.error.useful_error_message) {
            const errorModalRef = this.modalService.open(SimpleMessageModalComponent);
            errorModalRef.componentInstance.headerText = 'Cannot Cancel Requested Payment';
            errorModalRef.componentInstance.messageText = reason.error.useful_error_message;
            errorModalRef.componentInstance.messageAlertClass = 'alert-danger';
            this.loadBooking();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingPayment = false; });
    };
  }

  public handleMarkPaidOffline() {
    const amountModalRef = this.modalService.open(PrivateEventSelectPaymentAmountComponent);
    amountModalRef.componentInstance.booking = this.booking;
    amountModalRef.componentInstance.submitCallback = (amount?: number) => {
      const modalRef = this.modalService.open(SimpleMessageModalComponent);
      modalRef.componentInstance.headerText = 'Mark Event Paid Offline';
      modalRef.componentInstance.messageText = (amount === undefined)
        ? 'Are you sure you want to mark this event as paid offline? This action cannot be undone.'
        : 'Are you sure you want to record an offline payment of ' + this.orgConfig.getCurrencySymbol() + amount.toFixed(2) + ' for this event? This action cannot be undone.';
      modalRef.componentInstance.messageAlertClass = 'alert-info';
      modalRef.componentInstance.hasConfirmation = true;
      modalRef.componentInstance.confirmedCallback = () => {
        this.markPaidOffline(amount);
      };
    };
  }

  // undefined amount = pay the full amount due.
  private markPaidOffline(amount: number | undefined,
                          options: liveReaderRefusal.LiveReaderActionOptions = {}
                          ): Promise<liveReaderRefusal.LiveReaderCheckOutcome> {
    this.loadingPayment = true;

    return this.paymentService
      .makeOfflinePayment(this.booking.invoice_id, this.booking.booked_by_id, amount, options.bypass)
      .then((): liveReaderRefusal.LiveReaderCheckOutcome => {
        this.loadPrivateEvent(this.privateEvent.id.toString());
        return 'cleared';
      })
      .catch((reason): liveReaderRefusal.LiveReaderCheckOutcome => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (liveReaderRefusal.isLiveReaderUnconfirmedError(reason)) {
          if (options.recheck) { return 'live'; }
          liveReaderRefusal.openProceedAnywayModal(
            this.modalService,
            {
              message: liveReaderRefusal.getErrorMessageText(reason),
              checkAgain: () => this.markPaidOffline(amount, { recheck: true }),
              checking: false,
            },
            () => this.markPaidOffline(amount, { bypass: true })
          );
        } else if (liveReaderRefusal.isLiveReaderCapturedError(reason)) {
          liveReaderRefusal.openAlreadyPaidModal(
            this.modalService,
            liveReaderRefusal.getErrorMessageText(reason)
          );
          this.loadPrivateEvent(this.privateEvent.id.toString());
        } else if (reason.status === 400 && reason.error && reason.error.useful_error_message) {
          // E.g. another staff member marked the event paid offline first,
          // so there is no outstanding amount due.
          const errorModalRef = this.modalService.open(SimpleMessageModalComponent);
          errorModalRef.componentInstance.headerText = 'Cannot Mark Event Paid Offline';
          errorModalRef.componentInstance.messageText = reason.error.useful_error_message;
          errorModalRef.componentInstance.messageAlertClass = 'alert-danger';
          this.loadPrivateEvent(this.privateEvent.id.toString());
        } else {
          this.serverError = reason.status;
        }
        return 'other';
      })
      .finally(() => { this.loadingPayment = false; });
  }

}
